import React, { useCallback, useRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import { v4 as uuidv4 } from "uuid";
import Grid from "../../atoms/Grid/Grid";
import { getProductHistory } from "../../../store/slices/CustomerSlice/CustomerSlice";
import CartItemCounter from "../../atoms/CartItemCounter/CartItemCounter";
import { addCartItem } from "../../../store/slices/CartSlice/CartSlice";
import Button from "../../atoms/Button";
import { INVENTORY_CLASSIFICATION } from "../../../helpers/const";
import { willBackorder } from "../../../helpers/helperFunctions";
import BackorderConfirmModal from "../../atoms/BackorderConfirmModal/BackorderConfirmModal";

const productHistoryHeaderAttributes = [
  {
    id: "product_code",
    label: "Product Code",
    sortable: true,
    searchable: {
      label: "Search by Product Code or Description",
      type: "text",
      width: 4,
    },
    width: "10%",
  },
  {
    id: "description",
    label: "Description",
    sortable: true,
    width: "20%",
  },
  {
    id: "price",
    label: "Price",
    sortable: true,
    width: "8%",
  },
  {
    id: "shipping_weight",
    label: "Weight",
    sortable: true,
    width: "8%",
  },
  {
    id: "warehouse_availability",
    label: "Warehouse Availability",
    sortable: false,
    width: "20%",
  },
  {
    id: "total_ordered",
    label: "Total Qty Ordered",
    sortable: true,
    width: "10%",
  },
  {
    id: "last_order_date",
    label: "Last Order Date",
    sortable: true,
    width: "10%",
  },
  {
    id: "last_order_number",
    label: "Last Order #",
    sortable: true,
    width: "10%",
  },
  {
    id: "details",
    label: "Add to Cart",
    sortable: false,
    width: "10%",
  },
];

const returnRealAttributeName = (attr) => {
  switch (attr) {
    case "product_code":
      return "product_code";
    case "description":
      return "description";
    case "total_ordered":
      return "total_ordered";
    case "last_order_date":
      return "last_order_date";
    case "last_order_number":
      return "last_order_number";
    default:
      return attr;
  }
};

const fetchProductHistoryData = async (dispatch, params) => {
  const searchTerm = params.product_code || "";
  const sortBy = params.sortBy
    ? returnRealAttributeName(params.sortBy)
    : "total_ordered";
  const sortOrder = params.sortOrder === "asc" ? "" : "-";

  console.log(params);

  const result = await dispatch(
    getProductHistory({
      search: searchTerm,
      sort_by: `${sortOrder}${sortBy}`,
      page: params.page,
    })
  );

  if (!result?.error) {
    // The API now returns paginated data with count and results
    return {
      count: result.payload.count,
      results: result.payload.results,
    };
  }

  return { count: 0, results: [] };
};

const mapProductHistoryData = (
  products,
  cartItems,
  dispatch,
  updateCarts,
  requestAddCartItem
) => {
  // Helper function to get color class based on inventory level
  const getInventoryColorClass = (qty, cartQty = 0) => {
    let act_qty = qty - cartQty;
    if (act_qty <= 0) {
      return INVENTORY_CLASSIFICATION.OUT_OF_STOCK.bgColorClass;
    } else if (act_qty < 10) {
      return INVENTORY_CLASSIFICATION.LOW_STOCK.bgColorClass;
    } else {
      return INVENTORY_CLASSIFICATION.IN_STOCK.bgColorClass;
    }
  };

  // Helper function to get inventory status text
  const getInventoryStatusText = (qty, cartQty = 0) => {
    let act_qty = qty - cartQty;
    if (act_qty <= 0) {
      return INVENTORY_CLASSIFICATION.OUT_OF_STOCK.label;
    } else if (act_qty < 10) {
      return INVENTORY_CLASSIFICATION.LOW_STOCK.label;
    } else {
      return INVENTORY_CLASSIFICATION.IN_STOCK.label;
    }
  };

  return products.map((product) => {
    // Find matching cart item by product_code
    const cartItem = cartItems?.find(
      (item) => item.product_code === product.product_code
    );
    const cartQty = cartItem?.quantity || 0;

    const handleAddCartItem = () => {
      requestAddCartItem(product, cartQty);
    };

    // Render warehouse availability with same styling as ProductListItem
    const warehouseAvailability = product.qty_dict ? (
      <div className="flex gap-2 items-center flex-wrap">
        {Object.entries(product.qty_dict).map(([warehouse, qty]) => {
          const incomingQty = product.incoming_transfers?.[warehouse] || 0;
          return (
            <div
              key={warehouse}
              className="flex items-center gap-1 bg-white bg-opacity-80 px-1 py-0.5 rounded text-gray-800"
            >
              <span className="text-xs font-normal">{warehouse}:</span>
              <div
                className={`w-3 h-3 rounded-sm ${getInventoryColorClass(qty, cartQty)} ${qty > 0 && qty <= 5 ? "animate-pulse" : ""}`}
                title={`${warehouse}: ${getInventoryStatusText(qty, cartQty)}`}
              ></div>
              <span className="text-xs font-medium">{qty}</span>
              {incomingQty > 0 && (
                <>
                  <span className="text-xs text-gray-400">+</span>
                  <div
                    className={`w-3 h-3 rounded-sm ${INVENTORY_CLASSIFICATION.TRANSFER_INCOMING.bgColorClass}`}
                    title={`${warehouse}: ${incomingQty} ${INVENTORY_CLASSIFICATION.TRANSFER_INCOMING.label}`}
                  ></div>
                  <span
                    className={`text-xs font-medium ${INVENTORY_CLASSIFICATION.TRANSFER_INCOMING.colorClass}`}
                  >
                    {incomingQty}
                  </span>
                </>
              )}
            </div>
          );
        })}
      </div>
    ) : (
      <span className="text-gray-400">-</span>
    );

    return {
      id: product.product_code, // Use product_code as unique ID
      product_code: product.product_code || "-",
      description: product.description || "-",
      price:
        product.price === 0 || !product.price
          ? "-"
          : `$${(product.price || 0).toFixed(2)}`,
      shipping_weight: product.shipping_weight
        ? `${product.shipping_weight} lbs`
        : "-",
      warehouse_availability: warehouseAvailability,
      total_ordered: product.total_ordered || 0,
      last_order_date: product.last_order_date
        ? dayjs(product.last_order_date).format("MM/DD/YYYY")
        : "-",
      last_order_number: product.last_order_number || "-",
      details: !updateCarts ? (
        <span className="text-gray-400">-</span>
      ) : cartItem ? (
        <CartItemCounter cartItem={cartItem} />
      ) : (
        <button
          type="button"
          onClick={handleAddCartItem}
          className="rounded-lg bg-[var(--primary-color)] px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-[var(--secondary-color)]"
        >
          Add to Cart
        </button>
      ),
    };
  });
};

export function ItemHistory() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartItems } = useSelector((state) => state.cart);
  const { updateCarts, viewCarts } = useSelector((state) => state.admin);
  const [backorderConfirmProduct, setBackorderConfirmProduct] =
    useState(null);

  const dispatchAddCartItem = useCallback(
    (product) => {
      dispatch(
        addCartItem({
          product_code: product.product_code,
          last_price: product.price || 0,
          tariff_amount: product.tariff_amount || 0,
          product: {
            shipping_weight: product.shipping_weight || 0,
            description: product.description,
          },
        })
      );
    },
    [dispatch]
  );

  const requestAddCartItem = useCallback(
    (product, cartQty) => {
      if (willBackorder(product, cartQty)) {
        setBackorderConfirmProduct(product);
        return;
      }
      dispatchAddCartItem(product);
    },
    [dispatchAddCartItem]
  );

  const mapData = useCallback(
    (data) => {
      return mapProductHistoryData(
        data,
        cartItems,
        dispatch,
        updateCarts,
        requestAddCartItem
      );
    },
    [cartItems, dispatch, updateCarts, requestAddCartItem]
  );

  const goToCart = () => {
    navigate("/view-cart");
  };

  return (
    <div className="p-6">
      <div className="sm:flex sm:items-center mb-4">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold text-gray-900">
            Product Order History (Last Year)
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Quick reorder from your most frequently ordered products.
          </p>
        </div>
        {viewCarts && (
          <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
            <Button
              type="theme"
              onClick={goToCart}
              textContent={
                cartItems.length > 0
                  ? `View Cart (${cartItems.length})`
                  : "View Cart"
              }
            />
          </div>
        )}
      </div>
      <div className="mt-6">
        <Grid
          title=""
          headerAttributes={productHistoryHeaderAttributes}
          fetchDataFunction={fetchProductHistoryData}
          mapDataFunction={mapData}
          loadingSelector={(state) => state.customer?.showLoader}
          rowsPerPage={50}
          defaultSortBy="total_ordered"
          defaultSortOrder="desc"
          showFilters={true}
          showDateFilter={false}
          showPagination={true}
        />
      </div>

      <BackorderConfirmModal
        show={!!backorderConfirmProduct}
        onConfirm={() => {
          dispatchAddCartItem(backorderConfirmProduct);
          setBackorderConfirmProduct(null);
        }}
        onCancel={() => setBackorderConfirmProduct(null)}
      />
    </div>
  );
}
